import dayjs from 'dayjs';
import { normalizeFileName } from '../../../components/ui/utils/fileNameEncoding';

const checkboxColumn = (checked, label, rightMargin = 18) => ({
  width: 'auto',
  table: {
    widths: [16, 'auto'],
    body: [
      [
        {
          canvas: [
            {
              type: 'rect',
              x: 0,
              y: 0,
              w: 12,
              h: 12,
              r: 1,
              lineWidth: 1.2,
              lineColor: '#000000',
            },
            ...(checked
              ? [
                  {
                    type: 'polyline',
                    lineWidth: 1.4,
                    closePath: false,
                    color: '#0078D4',
                    points: [
                      { x: 1.8, y: 6.5 },
                      { x: 4.5, y: 9.5 },
                      { x: 10, y: 2.0 },
                    ],
                  },
                ]
              : []),
          ],
          margin: [0, 0, 4, 0],
        },
        { text: label, margin: [0, 0, rightMargin, 0] },
      ],
    ],
  },
  layout: 'noBorders',
});

/**
 * Export Returned Product Inspection (ΕΝ1 – Γ.Δ. 44/7) to PDF.
 * When includeAttachments is true, downloads a ZIP with the PDF + files.
 */
export const exportReturnedProductInspectionToPdf = async (
  record,
  includeAttachments = false,
) => {
  let missingFilesCount = 0;

  try {
    const pdfMake = await import('pdfmake/build/pdfmake');
    const pdfFonts = await import('pdfmake/build/vfs_fonts');
    const { API_BASE_URL, API_ENDPOINTS } = await import(
      '../../../config/apiConfig'
    );
    const ENTITY_TYPE_ID = 21;

    const vfs = pdfFonts.vfs || pdfFonts.default?.vfs || pdfFonts.default || {};
    if (Object.keys(vfs).length === 0) {
      console.warn('PDF vfs_fonts empty; fonts may not render.');
    }

    const logoImageKey = 'companyLogo';
    let companyLogoBase64 = null;

    try {
      const token = localStorage.getItem('authToken');
      const logoSettingResponse = await fetch(
        `${API_BASE_URL}/application-setting/key/companyLogo`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );

      if (logoSettingResponse.ok) {
        const logoSetting = await logoSettingResponse.json();
        if (logoSetting?.value && logoSetting.isActive !== false) {
          let logoUrl = logoSetting.value.trim();
          if (!logoUrl.startsWith('http://') && !logoUrl.startsWith('https://')) {
            const baseUrl = API_BASE_URL.replace('/api', '').replace(/\/$/, '');
            logoUrl = logoUrl.startsWith('/')
              ? `${baseUrl}${logoUrl}`
              : `${baseUrl}/${logoUrl}`;
          }

          try {
            const imageResponse = await fetch(logoUrl);
            if (imageResponse.ok) {
              const blob = await imageResponse.blob();
              const reader = new FileReader();
              companyLogoBase64 = await new Promise((resolve) => {
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });
            }
          } catch (imgError) {
            console.warn('Could not load company logo image:', imgError);
          }
        }
      }
    } catch (error) {
      console.warn('Could not load company logo:', error);
    }

    const fetchFileAsBase64 = async (fileId, fileName) => {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        const response = await fetch(
          API_ENDPOINTS.fileAttachment.download(fileId),
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(userId ? { 'user-id': userId } : {}),
            },
          },
        );
        if (!response.ok) return null;
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve({
              data: reader.result.split(',')[1],
              fileName,
              mimeType: blob.type || 'application/octet-stream',
            });
          };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch {
        return null;
      }
    };

    const fileAttachmentsData = [];
    let listedAttachments = Array.isArray(record.fileAttachments)
      ? record.fileAttachments.filter((f) => !f.isDeleted)
      : [];

    if (record?.id) {
      try {
        const token = localStorage.getItem('authToken');
        const userId = localStorage.getItem('userId');
        const filesResponse = await fetch(
          `${API_BASE_URL}/file-attachment/entity/${ENTITY_TYPE_ID}/${record.id}`,
          {
            headers: {
              'Content-Type': 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              ...(userId ? { 'user-id': userId } : {}),
            },
          },
        );
        if (filesResponse.ok) {
          const files = await filesResponse.json();
          listedAttachments = (Array.isArray(files) ? files : []).filter(
            (f) => !f.isDeleted,
          );
        }
      } catch (error) {
        console.warn('Error listing file attachments for PDF:', error);
      }
    }

    if (includeAttachments && listedAttachments.length > 0) {
      for (const file of listedAttachments) {
        const fileData = await fetchFileAsBase64(
          file.id,
          normalizeFileName(file.fileName || ''),
        );
        if (fileData) {
          fileAttachmentsData.push({ ...fileData, originalFile: file });
        } else {
          missingFilesCount += 1;
        }
      }
    }

    const formatDate = (date) => {
      if (!date) return '';
      try {
        return dayjs(date).format('DD/MM/YYYY');
      } catch {
        return '';
      }
    };

    const formatDateTime = (date) => {
      if (!date) return '';
      try {
        return dayjs(date).format('DD/MM/YYYY HH:mm');
      } catch {
        return '';
      }
    };

    const customerLabel = record?.customer
      ? [record.customer.name, record.customer.city].filter(Boolean).join(' — ')
      : '';

    const inspectedByLine = [
      record?.inspectedBy?.userDisplayName || '',
      record?.inspectedAt ? ` — ${formatDateTime(record.inspectedAt)}` : '',
    ].join('');

    const dispositionByLine = [
      record?.dispositionBy?.userDisplayName || '',
      record?.dispositionAt ? ` — ${formatDateTime(record.dispositionAt)}` : '',
    ].join('');

    const content = [
      {
        columns: [
          companyLogoBase64
            ? {
                image: logoImageKey,
                width: 120,
                alignment: 'left',
              }
            : { text: '', width: 120 },
          {
            text: 'ΕΝ1 – Γ.Δ. 44/7',
            alignment: 'right',
            fontSize: 11,
            color: '#666666',
            margin: [0, 0, 0, 10],
          },
        ],
        margin: [0, 0, 0, 20],
      },
      {
        canvas: [
          {
            type: 'line',
            x1: 0,
            y1: 0,
            x2: 515,
            y2: 0,
            lineWidth: 2,
            lineColor: '#000000',
          },
        ],
        margin: [0, -6, 0, 12],
      },
      {
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              {
                stack: [
                  {
                    text: 'RETURNED PRODUCT INSPECTION REPORT',
                    bold: true,
                    fontSize: 13,
                    alignment: 'center',
                  },
                  {
                    text: 'ΔΕΛΤΙΟ ΕΛΕΓΧΟΥ ΕΠΙΣΤΡΕΦΟΜΕΝΩΝ',
                    fontSize: 10,
                    alignment: 'center',
                    margin: [0, 4, 0, 0],
                  },
                ],
                colSpan: 4,
                style: 'titleCell',
              },
              {},
              {},
              {},
            ],
            [
              {
                text: '1. RECEIPT INSPECTION',
                colSpan: 4,
                bold: true,
                fillColor: '#f3f4f6',
                style: 'valueCell',
              },
              {},
              {},
              {},
            ],
            [
              { text: 'Item Code:', style: 'labelCell' },
              { text: record?.itemCode || '', style: 'valueCell' },
              { text: 'Product Name:', style: 'labelCell' },
              { text: record?.productName || '', style: 'valueCell' },
            ],
            [
              { text: 'Batch Number:', style: 'labelCell' },
              { text: record?.batchNumber || '', style: 'valueCell' },
              { text: 'Expiry Date:', style: 'labelCell' },
              {
                text: formatDate(record?.expiryDate),
                style: 'valueCell',
              },
            ],
            [
              { text: 'Quantity:', style: 'labelCell' },
              {
                text: record?.quantity?.toString() || '',
                style: 'valueCell',
              },
              { text: 'Customer:', style: 'labelCell' },
              { text: customerLabel, style: 'valueCell' },
            ],
            [
              { text: 'Shipment Date:', style: 'labelCell' },
              {
                text: formatDate(record?.shipmentDate),
                style: 'valueCell',
              },
              { text: 'Return Date:', style: 'labelCell' },
              {
                text: formatDate(record?.returnDate),
                style: 'valueCell',
              },
            ],
            [
              {
                stack: [
                  {
                    text: 'Cold-chain certificate (2–8°C) from customer:',
                    bold: true,
                    margin: [0, 0, 0, 8],
                  },
                  {
                    columns: [
                      checkboxColumn(record?.coldChainCertificate === 'YES', 'Yes'),
                      checkboxColumn(record?.coldChainCertificate === 'NO', 'No'),
                      checkboxColumn(record?.coldChainCertificate === 'NA', 'N/A', 0),
                    ],
                  },
                ],
                colSpan: 4,
                style: 'valueCell',
              },
              {},
              {},
              {},
            ],
            [
              { text: 'Temperature on receipt:', style: 'labelCell' },
              {
                text: record?.receiptTemperature || '',
                style: 'valueCell',
              },
              { text: 'Ice packs used:', style: 'labelCell' },
              {
                columns: [
                  checkboxColumn(record?.icePacksUsed === true, 'Yes'),
                  checkboxColumn(record?.icePacksUsed === false, 'No', 0),
                ],
                style: 'valueCell',
              },
            ],
            [
              {
                stack: [
                  {
                    text: 'Packaging appearance and contents:',
                    bold: true,
                    margin: [0, 0, 0, 8],
                  },
                  {
                    columns: [
                      checkboxColumn(
                        !!record?.packagingExcellentAppearance,
                        'Excellent appearance',
                      ),
                      checkboxColumn(
                        !!record?.packagingComplete,
                        'Complete packaging',
                      ),
                      checkboxColumn(
                        !!record?.packagingSealIntact,
                        'Authenticity seal intact',
                        0,
                      ),
                    ],
                  },
                ],
                colSpan: 4,
                style: 'valueCell',
              },
              {},
              {},
              {},
            ],
            [
              {
                stack: [
                  {
                    text: 'Verification:',
                    bold: true,
                    margin: [0, 0, 0, 8],
                  },
                  {
                    columns: [
                      checkboxColumn(record?.verification === 'YES', 'Yes'),
                      checkboxColumn(record?.verification === 'NO', 'No'),
                      checkboxColumn(record?.verification === 'NA', 'N/A', 0),
                    ],
                  },
                ],
                colSpan: 4,
                style: 'valueCell',
              },
              {},
              {},
              {},
            ],
            [
              {
                stack: [
                  {
                    text: 'Comments:',
                    bold: true,
                    margin: [0, 0, 0, 8],
                  },
                  {
                    table: {
                      widths: ['*'],
                      body: [
                        [
                          {
                            text: (record?.comments || '').trim() || ' ',
                            margin: [8, 8, 8, 40],
                          },
                        ],
                      ],
                    },
                    layout: {
                      hLineWidth: () => 1,
                      vLineWidth: () => 1,
                      hLineColor: () => '#d1d1d1',
                      vLineColor: () => '#d1d1d1',
                    },
                  },
                ],
                colSpan: 4,
                style: 'valueCell',
              },
              {},
              {},
              {},
            ],
            [
              {
                stack: [
                  {
                    text: 'Inspected By — Name / Signature / Date:',
                    bold: true,
                    margin: [0, 0, 0, 8],
                  },
                  { text: inspectedByLine || ' ' },
                  {
                    canvas: [
                      {
                        type: 'line',
                        x1: 0,
                        y1: 8,
                        x2: 455,
                        y2: 8,
                        lineWidth: 1,
                        lineColor: '#000000',
                      },
                    ],
                    margin: [0, 12, 0, 0],
                  },
                ],
                colSpan: 4,
                style: 'valueCell',
                margin: [10, 10, 10, 10],
              },
              {},
              {},
              {},
            ],
            [
              {
                text: '2. DISPOSITION',
                colSpan: 4,
                bold: true,
                fillColor: '#f3f4f6',
                style: 'valueCell',
              },
              {},
              {},
              {},
            ],
            [
              {
                columns: [
                  checkboxColumn(record?.disposition === 'RELEASE', 'Release for use'),
                  checkboxColumn(record?.disposition === 'DESTROY', 'Destroy', 0),
                ],
                colSpan: 4,
                style: 'valueCell',
              },
              {},
              {},
              {},
            ],
            [
              {
                stack: [
                  {
                    text: 'Disposition By — Name / Signature / Date:',
                    bold: true,
                    margin: [0, 0, 0, 8],
                  },
                  { text: dispositionByLine || ' ' },
                  {
                    canvas: [
                      {
                        type: 'line',
                        x1: 0,
                        y1: 8,
                        x2: 455,
                        y2: 8,
                        lineWidth: 1,
                        lineColor: '#000000',
                      },
                    ],
                    margin: [0, 12, 0, 0],
                  },
                ],
                colSpan: 4,
                style: 'valueCell',
                margin: [10, 10, 10, 10],
              },
              {},
              {},
              {},
            ],
          ],
        },
        layout: {
          hLineWidth: (i, node) =>
            i === 0 || i === node.table.body.length ? 2 : 1,
          vLineWidth: () => 1,
          hLineColor: () => '#000000',
          vLineColor: () => '#000000',
          paddingLeft: () => 10,
          paddingRight: () => 10,
          paddingTop: () => 10,
          paddingBottom: () => 10,
        },
        margin: [0, 0, 0, 0],
      },
    ];

    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [40, 60, 40, 50],
      defaultStyle: {
        font: 'Roboto',
        fontSize: 11,
        lineHeight: 1.4,
        color: '#000000',
      },
      header: (currentPage) => {
        if (companyLogoBase64 && currentPage > 1) {
          return {
            image: logoImageKey,
            width: 120,
            alignment: 'left',
            margin: [40, 20, 0, 0],
          };
        }
        return null;
      },
      footer: (currentPage, pageCount) => ({
        text: `Page ${currentPage} of ${pageCount} | Generated: ${dayjs().format('DD/MM/YYYY HH:mm')}`,
        alignment: 'center',
        fontSize: 8,
        color: '#999999',
        margin: [0, 10, 0, 0],
      }),
      content,
      images: companyLogoBase64
        ? {
            [logoImageKey]: companyLogoBase64,
          }
        : {},
      styles: {
        titleCell: {
          fontSize: 14,
          bold: true,
          alignment: 'center',
          padding: [15, 10, 15, 10],
        },
        labelCell: {
          fontSize: 11,
          bold: true,
          color: '#000000',
          padding: [10, 10, 10, 10],
        },
        valueCell: {
          fontSize: 11,
          color: '#000000',
          padding: [10, 10, 10, 10],
        },
      },
    };

    const pdfMakeApi = pdfMake.default || pdfMake;
    const pdfDoc = pdfMakeApi.createPdf(
      docDefinition,
      undefined,
      undefined,
      vfs,
    );
    const baseName = `Returned-Product-Inspection-${record?.id ?? 'report'}`;

    if (includeAttachments && fileAttachmentsData.length > 0) {
      try {
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        pdfDoc.getBlob((pdfBlob) => {
          zip.file(`${baseName}.pdf`, pdfBlob);
          fileAttachmentsData.forEach((fileData) => {
            const binaryString = atob(fileData.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i += 1) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            zip.file(fileData.fileName, bytes);
          });
          zip.generateAsync({ type: 'blob' }).then((zipBlob) => {
            const url = window.URL.createObjectURL(zipBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${baseName}-with-attachments.zip`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          });
        });
      } catch (error) {
        console.error('Error creating ZIP:', error);
        pdfDoc.download(`${baseName}.pdf`);
      }
    } else {
      pdfDoc.download(`${baseName}.pdf`);
    }

    return { success: true, missingFilesCount };
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw new Error('Failed to generate PDF. Please try again.');
  }
};
